// src/utils/backgroundGsap.ts
// GSAP-based background igniter. Drives PAN (slide) and KEN BURNS purely with GSAP.
// Works either with a precomputed decision (from backgroundPositionResolver)
// or by reading data-* attributes stamped on .bg-anim-sprite.

import type { BgDecision, SlideDecision, KenBurnsDecision, StaticDecision } from "./backgroundPositionResolver";

/**
 * Utility: convert normalized ms string (e.g., "12000ms") to seconds (number) for GSAP durations.
 */
function msToSec(ms: string | number | undefined, fallbackMs = 12000): number {
  if (ms == null) return fallbackMs / 1000;
  if (typeof ms === "number") return ms / 1000;
  const t = ms.toString().trim().toLowerCase();
  if (t.endsWith("ms")) return parseFloat(t) / 1000;
  if (t.endsWith("s")) return parseFloat(t);
  const n = Number(t);
  return Number.isFinite(n) ? n / 1000 : fallbackMs / 1000;
}

function splitXY(pos: string | null | undefined): { x: string; y: string } {
  const s = (pos || "50% 50%").trim();
  // Walk the string and split on the first whitespace that is not inside parentheses
  let depth = 0; let pivot = -1;
  for (let i = 0; i < s.length; i++) {
    const ch = s[i];
    if (ch === '(') depth++;
    else if (ch === ')') depth = Math.max(0, depth - 1);
    else if ((ch === ' ' || ch === '\t' || ch === '\n') && depth === 0) { pivot = i; break; }
  }
  if (pivot === -1) return { x: s, y: '50%' };
  const x = s.slice(0, pivot).trim();
  const y = s.slice(pivot).trim();
  return { x, y };
}

function getSprite(section: Element | null): HTMLElement | null {
  if (!section) return null;
  return section.querySelector<HTMLElement>(".bg-anim-sprite") || section.querySelector<HTMLElement>(".bg-image-layer");
}

function seedStatic(sprite: HTMLElement, xy: { x: string; y: string }) {
  sprite.style.backgroundPosition = `${xy.x} ${xy.y}`;
}

function applyVars(sprite: HTMLElement, vars: Record<string, string>) {
  for (const [k, v] of Object.entries(vars || {})) {
    sprite.style.setProperty(k, v);
  }
}

function igniteSlide(sprite: HTMLElement, d: SlideDecision) {
  // seed to start to avoid pop
  seedStatic(sprite, d.seed.position);
  applyVars(sprite, d.vars);

  const dur = msToSec(d.duration, 12000);
  const ease = d.ease || "power1.inOut";

  // Use backgroundPosition tween for pan (GSAP handles string → CSS nicely)
  // Some browsers require explicit x/y, so format as "x y" string.
  const toPos = `${d.to.x} ${d.to.y}`;
  const fromPos = `${d.from.x} ${d.from.y}`;

  // If backgroundPosition isn't identical to fromPos (e.g., previous state), set it first.
  const current = getComputedStyle(sprite).backgroundPosition.trim();
  if (current !== fromPos.trim()) {
    sprite.style.backgroundPosition = fromPos;
  }

  // @ts-ignore – gsap is global (loaded elsewhere)
  const gs = (window as any).gsap;
  if (!gs) { console.warn("[backgroundGsap] GSAP unavailable"); return null; }
  sprite.dataset.bgAnim = "pan";

  return gs.to(sprite, {
    duration: dur,
    ease,
    backgroundPosition: toPos,
    overwrite: true,
    force3D: false,
  });
}

function igniteKenBurns(sprite: HTMLElement, d: KenBurnsDecision) {
  // Seed transform origin and starting state.
  // We emulate pivot by setting transform-origin to the provided origin (x y)
  // and using scale on the sprite element.
  seedStatic(sprite, d.seed.position);
  applyVars(sprite, d.vars);

  const dur = msToSec(d.duration, 20000);
  const ease = (d as any).ease || d.vars["--kb-ease"] || "power1.inOut";

  sprite.style.transformOrigin = d.origin; // e.g., "50% 35%"
  sprite.style.willChange = "transform, background-position, opacity";

  // @ts-ignore – gsap is global (loaded elsewhere)
  const gs = (window as any).gsap;
  if (!gs) { console.warn("[backgroundGsap] GSAP unavailable"); return null; }
  sprite.dataset.bgAnim = "kenburns";

  // Optionally adjust backgroundPosition too if x/y changed across from→to
  const fromPos = `${d.from.x} ${d.from.y}`;
  const toPos = `${d.to.x} ${d.to.y}`;
  if (fromPos !== toPos) {
    sprite.style.backgroundPosition = fromPos;
    gs.to(sprite, { backgroundPosition: toPos, duration: dur, ease, overwrite: true });
  }

  // Scale tween (primary ken burns effect)
  const tl = gs.timeline({ defaults: { overwrite: true } });
  tl.set(sprite, { scale: parseFloat(String(d.from.scale || 1)) });
  tl.to(sprite, { scale: parseFloat(String(d.to.scale || 1)), duration: dur, ease });
  return tl;
}

function igniteStatic(sprite: HTMLElement, d: StaticDecision) {
  seedStatic(sprite, d.seed.position);
  applyVars(sprite, d.vars);
  sprite.dataset.bgAnim = "static";
  return null;
}

/**
 * Build a decision by reading data-* attributes from the sprite, if present.
 * Useful when you don't have the resolver output handy.
 */
export function inferDecisionFromAttrs(section: HTMLElement): BgDecision | null {
  const sprite = getSprite(section);
  if (!sprite) return null;
  const kind = sprite.getAttribute("data-bg-anim") || sprite.getAttribute("data-anim") || "";
  if (kind === "pan" || kind === "slide") {
    const fromXY = splitXY(sprite.getAttribute("data-pan-start"));
    const toXY   = splitXY(sprite.getAttribute("data-pan-stop"));
    const duration = sprite.getAttribute("data-pan-duration-ms") || sprite.getAttribute("data-pan-duration") || "12000ms";
    const ease = sprite.getAttribute("data-pan-ease") || "power1.inOut";
    return {
      kind: "slide",
      from: { x: fromXY.x, y: fromXY.y },
      to:   { x: toXY.x,   y: toXY.y },
      duration: String(duration),
      ease: String(ease),
      seed: { position: { x: fromXY.x, y: fromXY.y } },
      vars: {},
    };
  }
  if (kind === "kenburns") {
    const origin = (sprite.getAttribute("data-kb-origin") || "50% 50%").trim();
    const [ox, oy] = origin.split(/\s+/);
    const kbEase = sprite.getAttribute('data-kb-ease') || sprite.getAttribute('data-background-animation-ease') || 'power1.inOut';
    const fromScaleAttr = sprite.getAttribute('data-kb-scale-start') || sprite.getAttribute('data-kb-from-scale') || '1';
    const toScaleAttr   = sprite.getAttribute('data-kb-scale-end')   || sprite.getAttribute('data-kb-to-scale')   || '1.12';
    const duration = sprite.getAttribute("data-kb-duration") || sprite.getAttribute("data-background-animation-duration") || "20000ms";
    const mode = (Number(toScaleAttr) > Number(fromScaleAttr) ? "in" : "out") as KenBurnsDecision["mode"];
    return {
      kind: "kenburns",
      mode,
      origin,
      from: { x: ox, y: oy, scale: String(fromScaleAttr) },
      to:   { x: ox, y: oy, scale: String(toScaleAttr) },
      duration: String(duration),
      ease: String(kbEase),
      seed: { position: { x: ox, y: oy }, scale: String(fromScaleAttr) },
      vars: { "--kb-origin": origin },
    } as KenBurnsDecision;
  }
  // fallback: static
  const posStr = sprite.style.backgroundPosition || getComputedStyle(sprite).backgroundPosition || '50% 50%';
  const xy = splitXY(posStr);
  return {
    kind: 'static',
    position: `${xy.x} ${xy.y}`,
    seed: { position: { x: xy.x, y: xy.y } },
    vars: {},
  } as StaticDecision;
}

/**
 * Ignite GSAP background animation for a section.
 * - If `decision` is provided, it takes precedence.
 * - Otherwise, it will attempt to infer from DOM data-* attributes.
 * Returns a GSAP timeline/tween or null if static/gsap missing.
 */
export function igniteBackground(section: HTMLElement, decision?: BgDecision) {
  const sprite = getSprite(section);
  if (!sprite) { console.warn("[backgroundGsap] No sprite in section"); return null; }
  const d = decision || inferDecisionFromAttrs(section);
  if (!d) { console.warn("[backgroundGsap] No decision for section"); return null; }

  switch (d.kind) {
    case "slide":
      return igniteSlide(sprite, d);
    case "kenburns":
      return igniteKenBurns(sprite, d);
    case "static":
      return igniteStatic(sprite, d);
    default:
      return null;
  }
}